/**
 * CR316: Components Panel - Typography & Config for Deal Card Components
 * CR323: Replaced Ribbon toggles with RibbonComposer
 * CRRIBBON: Added RibbonStylingSection for HEX-based color control
 * 
 * Consolidates configuration for:
 * - Header (Front) - typography + display controls
 * - Header (Back) - merchant logo/name + details line
 * - Banners:
 *   - Starburst - value + name styling
 *   - Ribbon - RibbonComposer (primary line, merchant subline, typography) + Styling (HEX colors)
 * 
 * Visibility chips show Structure truth (enabled/disabled from Structure tab).
 * No enable/disable toggles here - Components only controls styling.
 */

import { useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  LayoutTemplate, 
  Star, 
  CheckCircle2,
  XCircle,
} from "lucide-react";
import type { ComponentsConfig, ComponentTypography, ComponentConfig } from "./DealCardsTab";
import { 
  RibbonComposer, 
  DEFAULT_RIBBON_COMPOSER_CONFIG,
  type RibbonComposerConfig,
} from "@/components/admin/deal-cards/RibbonComposer";
import { 
  RibbonStylingSection,
  type RibbonStylingConfig,
} from "@/components/admin/deal-cards/RibbonStylingSection";

// ================================================================
// TYPES
// ================================================================

export interface ComponentsPanelProps {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
  // Structure truth - for visibility chips
  structureToggles?: {
    ribbon?: boolean;
    starburst?: boolean;
    header?: boolean;
    back_header?: boolean;
  };
  // CR323: Optional sample deal for ribbon preview (raw data from DB)
  sampleDeal?: Record<string, unknown> | null;
  // CR323: Deal type key for label derivation
  dealTypeKey?: string;
  // CRRIBBON: Ribbon styling overrides
  ribbonStyling?: RibbonStylingConfig;
  onRibbonStylingChange?: (update: Partial<RibbonStylingConfig>) => void;
  onClearRibbonBase?: () => void;
  onClearRibbonBorder?: () => void;
}

// Font family options
const FONT_FAMILIES = [
  { value: "inherit", label: "Default (inherit)" },
  { value: "Inter", label: "Inter" },
  { value: "DM Sans", label: "DM Sans" },
  { value: "Space Grotesk", label: "Space Grotesk" },
  { value: "Manrope", label: "Manrope" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Source Sans 3", label: "Source Sans 3" },
];

// Font weight options
const FONT_WEIGHTS = [
  { value: 400, label: "Regular (400)" },
  { value: 500, label: "Medium (500)" },
  { value: 600, label: "Semibold (600)" },
  { value: 700, label: "Bold (700)" },
];

// ================================================================
// HELPERS
// ================================================================

function VisibilityChip({ enabled, label }: { enabled: boolean; label?: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={enabled ? "default" : "secondary"} 
            className={`text-[10px] px-1.5 py-0 h-5 ${enabled ? 'bg-green-600 hover:bg-green-600' : 'bg-muted text-muted-foreground'}`}
          >
            {enabled ? (
              <><CheckCircle2 className="h-3 w-3 mr-0.5" /> On</>
            ) : (
              <><XCircle className="h-3 w-3 mr-0.5" /> Off</>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[200px]">
          <p className="text-xs">Visibility controlled in Structure → Front/Back</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TypographyControls({
  typography,
  onChange,
  label,
}: {
  typography?: ComponentTypography;
  onChange: (update: ComponentTypography) => void;
  label: string;
}) {
  const fontFamily = typography?.fontFamily ?? "inherit";
  const fontSize = typography?.fontSize ?? 14;
  const fontWeight = typography?.fontWeight ?? 500;

  return (
    <div className="space-y-2 p-3 bg-muted/40 rounded-md">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Font</Label>
          <Select 
            value={fontFamily} 
            onValueChange={(v) => onChange({ ...typography, fontFamily: v })}
          >
            <SelectTrigger className="h-7 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((f) => (
                <SelectItem key={f.value} value={f.value} className="text-xs">
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Size</Label>
          <Input
            type="number"
            value={fontSize}
            onChange={(e) => onChange({ ...typography, fontSize: parseInt(e.target.value) || 14 })}
            className="h-7 text-[11px]"
            min={8}
            max={48}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Weight</Label>
          <Select 
            value={String(fontWeight)} 
            onValueChange={(v) => onChange({ ...typography, fontWeight: parseInt(v) })}
          >
            <SelectTrigger className="h-7 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_WEIGHTS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)} className="text-xs">
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function SubElementConfig({
  config,
  onChange,
  label,
  showTypography = true,
}: {
  config?: ComponentConfig;
  onChange: (update: ComponentConfig) => void;
  label: string;
  showTypography?: boolean;
}) {
  const enabled = config?.enabled ?? true;

  return (
    <div className="space-y-2 p-3 bg-background rounded border">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">Display intent</span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => onChange({ ...config, enabled: v })}
          />
        </div>
      </div>
      {showTypography && enabled && (
        <TypographyControls
          typography={config?.typography}
          onChange={(typo) => onChange({ ...config, typography: typo })}
          label="Typography"
        />
      )}
    </div>
  );
}

// ================================================================
// HELPER: Convert between RibbonComposerConfig and ComponentsConfig.ribbon
// ================================================================

function toRibbonComposerConfig(ribbonConfig: ComponentsConfig["ribbon"]): RibbonComposerConfig {
  if (!ribbonConfig) {
    return { ...DEFAULT_RIBBON_COMPOSER_CONFIG };
  }
  
  // CR323: Check if we have the new composer format
  const composerRibbon = ribbonConfig as unknown as RibbonComposerConfig;
  if (composerRibbon.primary && composerRibbon.typography) {
    return composerRibbon;
  }
  
  // Legacy: Convert from old format
  return {
    ...DEFAULT_RIBBON_COMPOSER_CONFIG,
    position: ribbonConfig.position,
    offsetX: ribbonConfig.offsetX,
    offsetY: ribbonConfig.offsetY,
  };
}

function fromRibbonComposerConfig(config: RibbonComposerConfig): ComponentsConfig["ribbon"] {
  // Store the full composer config, but maintain backward compatibility shape
  return config as unknown as ComponentsConfig["ribbon"];
}

// ================================================================
// COMPONENT
// ================================================================

export function ComponentsPanel({
  components,
  onChange,
  structureToggles = {},
  sampleDeal,
  dealTypeKey = "BONUS",
  // CRRIBBON: Ribbon styling props
  ribbonStyling,
  onRibbonStylingChange,
  onClearRibbonBase,
  onClearRibbonBorder,
}: ComponentsPanelProps) {
  // Merge structure defaults
  const headerEnabled = structureToggles.header ?? true;
  const backHeaderEnabled = structureToggles.back_header ?? true;
  const ribbonEnabled = structureToggles.ribbon ?? true;
  const starburstEnabled = structureToggles.starburst ?? true;
  
  // CR323: Get ribbon composer config
  const ribbonComposerConfig = useMemo(() => {
    return toRibbonComposerConfig(components.ribbon);
  }, [components.ribbon]);
  
  const handleRibbonComposerChange = useCallback((config: RibbonComposerConfig) => {
    onChange({
      ribbon: fromRibbonComposerConfig(config),
    });
  }, [onChange]);
  
  // CRRIBBON: Check if we have styling overrides
  const hasRibbonBaseOverride = !!ribbonStyling?.ribbonBaseHex;
  const hasRibbonBorderOverride = !!ribbonStyling?.ribbonBorderHex;

  return (
    <div className="space-y-6">
      {/* ============================================================ */}
      {/* HEADER (FRONT) */}
      {/* ============================================================ */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-semibold">Header (Front)</Label>
          </div>
          <VisibilityChip enabled={headerEnabled} />
        </div>
        <p className="text-xs text-muted-foreground">
          Typography and display settings for the front face header.
        </p>
        <TypographyControls
          typography={components.header_front?.typography}
          onChange={(typo) => onChange({ 
            header_front: { ...components.header_front, typography: typo } 
          })}
          label="Header Typography"
        />
      </section>

      <Separator />

      {/* ============================================================ */}
      {/* HEADER (BACK) */}
      {/* ============================================================ */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-semibold">Header (Back)</Label>
          </div>
          <VisibilityChip enabled={backHeaderEnabled} />
        </div>
        <p className="text-xs text-muted-foreground">
          Controls for the card back header: logo, name, and details line.
        </p>
        
        {/* Merchant Logo */}
        <SubElementConfig
          config={components.header_back?.merchantLogo}
          onChange={(cfg) => onChange({
            header_back: { ...components.header_back, merchantLogo: cfg }
          })}
          label="Merchant Logo"
          showTypography={false}
        />
        
        {/* Merchant Name */}
        <SubElementConfig
          config={components.header_back?.merchantName}
          onChange={(cfg) => onChange({
            header_back: { ...components.header_back, merchantName: cfg }
          })}
          label="Merchant Name"
          showTypography={true}
        />
        
        {/* Details Line */}
        <SubElementConfig
          config={components.header_back?.detailsLine}
          onChange={(cfg) => onChange({
            header_back: { ...components.header_back, detailsLine: cfg }
          })}
          label="Details Line"
          showTypography={true}
        />
      </section>

      <Separator />

      {/* ============================================================ */}
      {/* BANNERS */}
      {/* ============================================================ */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-semibold">Banners</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Configure starburst and ribbon appearance (visibility controlled in Structure).
        </p>

        {/* -------------------------------- */}
        {/* STARBURST */}
        {/* -------------------------------- */}
        <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-primary" />
              <Label className="text-sm font-medium">Starburst</Label>
            </div>
            <VisibilityChip enabled={starburstEnabled} />
          </div>

          {/* Value (styling only) */}
          <SubElementConfig
            config={components.starburst?.value}
            onChange={(cfg) => onChange({
              starburst: { ...components.starburst, value: cfg }
            })}
            label="Value (styling)"
            showTypography={true}
          />

          {/* Name/Label */}
          <SubElementConfig
            config={components.starburst?.name}
            onChange={(cfg) => onChange({
              starburst: { ...components.starburst, name: cfg }
            })}
            label="Name Label"
            showTypography={true}
          />
        </div>

        {/* -------------------------------- */}
        {/* RIBBON - CR323: Full RibbonComposer */}
        {/* -------------------------------- */}
        <RibbonComposer
          config={ribbonComposerConfig}
          onChange={handleRibbonComposerChange}
          structureEnabled={ribbonEnabled}
          sampleDeal={sampleDeal}
          dealTypeKey={dealTypeKey}
        />
        
        {/* -------------------------------- */}
        {/* RIBBON STYLING - CRRIBBON: HEX-based color control */}
        {/* -------------------------------- */}
        {onRibbonStylingChange && (
          <RibbonStylingSection
            styling={ribbonStyling ?? {}}
            onChange={onRibbonStylingChange}
            onClearBase={onClearRibbonBase ?? (() => {})}
            onClearBorder={onClearRibbonBorder ?? (() => {})}
            hasBaseOverride={hasRibbonBaseOverride}
            hasBorderOverride={hasRibbonBorderOverride}
            dealTypeKey={dealTypeKey}
          />
        )}
      </section>
    </div>
  );
}

export default ComponentsPanel;
